import ProductCard from "@/components/Ui/Pages/ProductCard/ProductCard";
import Spinner from "@/components/Ui/Tools/Spinner/Spinner";
import { useCount } from "@/global-state/global.state";
import { SITE_NAME } from "@/helper/constant";
import { t } from "@/helper/helper";
import useSearch from "@/hooks/pages/search/useSearch";
import useInterSectionObserver from "@/hooks/tools/useInterSectionObserver";
import { IBrandsGeneric, IProductDataGeneric } from "global-types/global.types";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { useEffect, useState } from "react";
import styled from "styled-components";

interface Props {
  locale: string;
  q: string;
}

export default function Search({ locale, q }: Props) {
  const [paginateData, setPaginateData] = useState<
    IProductDataGeneric<IBrandsGeneric>[]
  >([]);

  const { count, setCount, destroyCount } = useCount();

  const { data, isFetching } = useSearch({
    setPaginationData: setPaginateData,
    params: {
      page: count > 1 ? count : null,
      textQuery: q,
    },
  });

  const [view, setView] = useState<boolean>(false);

  useInterSectionObserver({
    elementClassName: ".product-card",
    render: paginateData,
    setView,
  });

  useEffect(() => {
    if (data && data.data.length !== 0) {
      if (count <= data?.pagination?.last_page && !isFetching) {
        setCount(1);
      }
    }
  }, [view]);

  useEffect(() => {
    setPaginateData([]);
    destroyCount();

    return () => {
      destroyCount();
      setPaginateData([]);
    };
  }, [q, destroyCount]);

  return (
    <>
      <Head>
        <title>{`${SITE_NAME} | ${q}`}</title>
      </Head>
      <Wrapper>
        <div className="search-product-list">
          <div className="search-product-list-body">
            <div className="search-main-section">
              <div className="result search-productlist-header">
                <Flex>
                  <div className="breadcrumb">
                    <a href="/">Home page</a>
                    <span className="breadcrumb-seperator" />
                    <a href="/search">{t("search", locale)}</a>
                  </div>

                  {!isFetching ? (
                    <span>{data?.pagination.total} result</span>
                  ) : (
                    <></>
                  )}
                </Flex>
                <H1>{q}</H1>
              </div>

              {count < 2 && isFetching ? (
                <SpinnerBox>
                  <Spinner />
                </SpinnerBox>
              ) : (
                <>
                  {paginateData.map((item, index) => (
                    <ProductCard
                      slug={""}
                      locale={locale}
                      data={item}
                      md=""
                      key={index.toString() + item.title}
                      from="search"
                    />
                  ))}
                </>
              )}
            </div>
          </div>
          {paginateData.length === 0 && !isFetching ? (
            <NotFound>{t("resultNotFound", locale)}</NotFound>
          ) : (
            ""
          )}
          {isFetching && count > 1 ? (
            <SpinnerPagination>
              <Spinner />
            </SpinnerPagination>
          ) : (
            <></>
          )}
        </div>
      </Wrapper>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { locale } = ctx;
  const { q } = ctx.query;

  try {
    if (q === undefined || q === "") {
      return {
        redirect: {
          permanent: false,
          destination: "/",
        },
      };
    }

    return {
      props: {
        locale,
        q,
      },
    };
  } catch (error) {
    return {
      redirect: {
        permanent: false,
        destination: "/",
      },
    };
  }
};

const Wrapper = styled.div`
  .search-product-list {
    position: relative;
    width: 100%;
    min-height: 350px;

    .search-product-list-body {
      width: 1289px;
      margin: 0 auto;
      padding-bottom: 7rem;
      display: grid;
      grid-template-columns: repeat(8, 1fr);
      grid-gap: 0 1.6rem;

      .search-productlist-header {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      @media screen and (max-width: 1199px) {
        width: 100%;
        padding: 0 1.6rem;
      }

      .mobile-sidebar-header {
        display: none;
      }

      .breadcrumb {
        padding-top: 0;
      }

      .mobile-sidebar {
        grid-column: 1/3;
        background-color: #fff;

        .filter-header {
          display: flex;
          padding: 2.4rem 2.4rem 0 2.4rem;
          margin-bottom: 2.4rem;

          .filter-header-wrapper {
            width: 100%;
            border-bottom: 1px solid #c4c4c4;
            padding-bottom: 2.4rem;
            display: flex;
            align-items: center;
            justify-content: space-between;

            p {
              font-style: normal;
              font-weight: normal;
              font-size: 1.4rem;
              line-height: 100%;
              color: #000000;
            }

            button {
              cursor: pointer;
              font-style: normal;
              font-weight: 300;
              font-size: 1.2rem;
              line-height: 100%;
              color: #8b8e97;

              &:hover {
                color: #000000;
              }
            }
          }
        }
      }

      .search-main-section {
        grid-column: 1/-1;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        grid-gap: 2.4rem 1.6rem;
        align-content: flex-start;
        height: fit-content;

        .result {
          grid-column: 1/-1;
          width: 100%;
          // border-bottom: 2px solid rgba($color: #D0D4DE, $alpha: .25);
          display: flex;
          align-items: center;
          justify-content: space-between;

          span {
            font-style: normal;
            font-weight: normal;
            font-size: 18px;
            line-height: 125%;
            color: #222222;
            display: flex;
            align-items: center;
            white-space: nowrap;
          }
        }
      }
    }

    .sidebar-category-card {
      background: #ffffff;
      box-shadow: 0px 0px 24px rgba(27, 28, 31, 0.06);
      border-radius: 6px;
      padding: 3.6rem;
      margin-bottom: 3.6rem;

      .checkbox-group {
        margin-bottom: 1.4rem;
      }
    }

    @media screen and (max-width: 767.9px) {
      .search-product-list-body {
        grid-template-columns: repeat(1, 1fr);
        grid-gap: 0;

        .search-main-section {
          grid-column: 3/-3;
          // grid-template-columns: repeat(1, 1fr) !important;
          padding: 24px 0 96px;
        }

        // .side-section {
        //   visibility: hidden;
        //   // position: absolute;
        //   // z-index: 99;
        // }
      }

      .mobile-sidebar-header {
        display: flex !important;
        border: 1px solid #d0d4de;
        box-sizing: border-box;
        padding: 16px 24px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 24px;

        h5 {
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          line-height: 16px;
          color: #1b1c1f;
          display: flex;
          align-items: center;

          svg {
            margin-right: 6px;
          }
        }

        p {
          font-style: normal;
          font-weight: normal;
          font-size: 14px;
          line-height: 21px;
          color: #ff4c4c;

          svg {
            margin-left: 60px;
          }
        }
      }

      .result {
        display: none !important;
      }
    }

    .search-not-found {
      grid-column: 1/-1;
      display: grid;
      text-align: center;
      width: 100%;
      font-size: 24px;
    }
  }
`;

const Flex = styled.div`
  display: flex;
  width: 100%;
`;

const H1 = styled.h1`
  font-size: 30px;
  color: #222;
  text-transform: capitalize;
  width: 100%;
  margin-top: 50px;
`;

const SpinnerBox = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const SpinnerPagination = styled.div`
  display: flex;
  justify-content: center;

  img {
    width: 50px;
    height: 50px;
  }
`;

const NotFound = styled.div`
  display: flex;
  justify-content: center;
  font-size: 30px;
  width: 100%;
`;
